import { useState } from "react";
import { useStock } from "../App"; 
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

const getStockStatus = (stock, minStock) => {
  if (stock === 0) return { label: "Out of Stock", bg: "#fee2e2", color: "#b91c1c" };
  if (stock <= minStock) return { label: "Low Stock", bg: "#fef9c3", color: "#a16207" };
  return { label: "In Stock", bg: "#dcfce7", color: "#15803d" };
};

const Icons = {
  Total: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>,
  InStock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>,
  LowStock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>,
  OutStock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
};

export default function StockPage() {
  const { products, balances, deductBalance } = useStock(); 
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  
  const today = new Date().toISOString().split('T')[0];
  const [showModal, setShowModal] = useState(false);
  
  const [formData, setFormData] = useState({ 
    name: "", totalCost: "", sellingPrice: "", stock: "", 
    lowLimit: "", unit: "kg", method: "", date: today 
  });

  const filtered = products.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) || 
    (s.id && s.id.toLowerCase().includes(search.toLowerCase()))
  );

  const outOfStock = products.filter((s) => s.stock === 0).length;
  const lowStock   = products.filter((s) => s.stock > 0 && s.stock <= s.lowLimit).length;
  const inStock    = products.filter((s) => s.stock > s.lowLimit).length;

  const totalStockValue = products.reduce((sum, p) => {
    const stockQty = parseFloat(p.stock) || 0;
    const unitCost = parseFloat(p.unitCost) || 0;
    return sum + (stockQty * unitCost);
  }, 0);

  const perUnitCost = (formData.totalCost && formData.stock) 
    ? (parseFloat(formData.totalCost) / parseFloat(formData.stock)).toFixed(2) 
    : "0.00";

  const handleAddStock = async (e) => {
    e.preventDefault();
    if (!formData.method) return alert("Please select a wallet to deduct payment from.");
    const costAmount = parseFloat(formData.totalCost);

    if (balances[formData.method] < costAmount) {
      alert(`Insufficient balance in ${formData.method}!`);
      return;
    }

    setLoading(true);
    try {
      await deductBalance(formData.method, costAmount);
      const customLowLimit = formData.lowLimit !== "" ? parseFloat(formData.lowLimit) : 5;

      const newProduct = {
        ...formData,
        id: `PRD-${Math.floor(1000 + Math.random() * 9000)}`,
        unitCost: perUnitCost,
        price: `¥${formData.sellingPrice}`,
        lowLimit: customLowLimit,
        createdAt: new Date() 
      };

      // 1. Add to Products Collection
      await addDoc(collection(db, "products"), newProduct);

      // 2. Add to Sales Collection (for Ledger)
      const now = new Date();
      await addDoc(collection(db, "sales"), {
        id: `#STK-${Math.floor(1000 + Math.random() * 9000)}`,
        customer: "System",
        itemsSummary: `Stock Added: ${formData.name} (x${formData.stock} ${formData.unit})`,
        amount: `¥${costAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}`,
        amountNum: costAmount,
        status: "Stock", 
        date: formData.date,
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        wallet: formData.method,
        createdAt: now
      });

      setShowModal(false);
      setFormData({ name: "", totalCost: "", sellingPrice: "", stock: "", lowLimit: "", unit: "kg", method: "", date: today });
      alert("Stock added successfully!");
    } catch (err) {
      alert("Error saving to cloud: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Wallets & Stats */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {Object.entries(balances).map(([method, amount]) => (
          <div key={method} style={{ background: "#1e293b", color: "white", padding: "16px 20px", borderRadius: 14, flex: "1 1 200px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{method}</span>
            <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "system-ui, sans-serif" }}>¥{amount.toLocaleString()}</span>
          </div>
        ))}
        <div style={{ background: "linear-gradient(135deg, #0f172a, #1e1b4b)", color: "white", padding: "16px 20px", borderRadius: 14, flex: "1 1 200px", display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #3730a3" }}>
            <span style={{ fontSize: 13, color: "#a5b4fc", fontWeight: 600 }}>Total Stock Value</span>
            <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "system-ui, sans-serif", color: "#818cf8" }}>
              ¥{totalStockValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        {[
          { label: "Total Products", value: products.length, icon: Icons.Total, color: "#6366f1", bg: "#eef2ff" },
          { label: "In Stock", value: inStock, icon: Icons.InStock, color: "#10b981", bg: "#d1fae5" },
          { label: "Low Stock", value: lowStock, icon: Icons.LowStock, color: "#f59e0b", bg: "#fef3c7" },
          { label: "Out of Stock", value: outOfStock, icon: Icons.OutStock, color: "#ef4444", bg: "#fee2e2" },
        ].map((s, i) => (
          <div key={i} style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>{s.label}</span>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: s.bg, color: s.color, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 600, color: "#0f172a" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Stock Table */}
      <div style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", margin: 0 }}>Stock Management</p>
            <button onClick={() => setShowModal(true)} style={{ background: "#6366f1", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600, marginTop: 12 }}>+ Add to Stock</button>
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search stock..." style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
        </div>

        <div className="table-responsive">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {["Product", "In Stock", "Unit Cost", "Status", "Date"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 11, padding: "8px" }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => {
                const st = getStockStatus(s.stock, s.lowLimit);
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f8fafc" }}>
                    <td style={{ padding: "12px 8px" }}>{s.name}</td>
                    <td style={{ padding: "12px 8px" }}>{s.stock} {s.unit}</td>
                    <td style={{ padding: "12px 8px" }}>¥{s.unitCost}</td>
                    <td style={{ padding: "12px 8px" }}><span style={{ ...st, padding: "4px 8px", borderRadius: 6 }}>{st.label}</span></td>
                    <td style={{ padding: "12px 8px" }}>{s.date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {products.length === 0 && <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 20 }}>No stock available.</p>}
        </div>
      </div>

      {/* =======================
          ADD TO STOCK MODAL
      ======================= */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
          <div style={{ background: "white", padding: 32, borderRadius: 20, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}>
            <h3 style={{ margin: "0 0 24px", color: "#0f172a", fontSize: 20, fontWeight: 700 }}>Add New Stock</h3>
            
            <form onSubmit={handleAddStock} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label style={labelStyle}>Product Name *</label>
                  <input required placeholder="e.g. Item Name" style={inputStyle} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label style={labelStyle}>Date *</label>
                  <input type="date" required style={inputStyle} value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                </div>
                <div>
                  <label style={labelStyle}>Quantity *</label>
                  <input type="number" required placeholder="0" style={inputStyle} value={formData.stock} onChange={e => setFormData({...formData, stock: e.target.value})} />
                </div>
                <div>
                  <label style={labelStyle}>Unit</label>
                  <select style={inputStyle} value={formData.unit} onChange={e => setFormData({...formData, unit: e.target.value})}>
                    <option value="kg">kg</option>
                    <option value="pcs">pcs</option>
                    <option value="ltr">ltr</option>
                    <option value="box">box</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Total Cost (¥) *</label>
                  <input type="number" step="0.01" required placeholder="0.00" style={inputStyle} value={formData.totalCost} onChange={e => setFormData({...formData, totalCost: e.target.value})} />
                </div>
                <div>
                  <label style={labelStyle}>Selling Price (¥) *</label>
                  <input type="number" step="0.01" required placeholder="0.00" style={inputStyle} value={formData.sellingPrice} onChange={e => setFormData({...formData, sellingPrice: e.target.value})} />
                </div>
                <div>
                  <label style={labelStyle}>Payment Wallet *</label>
                  <select required style={inputStyle} value={formData.method} onChange={e => setFormData({...formData, method: e.target.value})}>
                    <option value="">Select Wallet...</option>
                    {balances && Object.keys(balances).map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Low Stock Alert</label>
                  <input type="number" placeholder="Default: 5" style={inputStyle} value={formData.lowLimit} onChange={e => setFormData({...formData, lowLimit: e.target.value})} />
                </div>
              </div>

              <div style={{ padding: "12px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", marginTop: 4, border: "1px dashed #cbd5e1" }}>
                Auto Per Unit Cost: <strong style={{ color: "#0f172a", fontSize: 15 }}>¥ {perUnitCost}</strong>
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "white", color: "#64748b", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#4f46e5", color: "white", fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
                  {loading ? "Adding..." : "Add Stock"}
                </button>
              </div>
            </form>
            
          </div>
        </div>
      )}

    </div>
  );
}